import {
  CrisisStatus,
  ICrisisCallTeamMember,
  ICrisisCallsAllocatedClientsList,
} from '~/services/api/crisis/types';

const getAllocationKey = (allocation: ICrisisCallsAllocatedClientsList): string =>
  `${allocation.clientId}_${allocation.crisisCallId ?? ''}_${allocation.isDispatch}`;

export const getAllocationClientId = (
  allocation: ICrisisCallsAllocatedClientsList,
): number | undefined => allocation.allocationClientId;

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseLocalAllocationDate = (date: string): Date => {
  const dateOnlyMatch = DATE_ONLY_PATTERN.exec(date);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;

    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  return new Date(date);
};

const isTodayInLocalTimezone = (date?: string): boolean => {
  if (!date) {
    return false;
  }

  const parsedDate = parseLocalAllocationDate(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return false;
  }

  const today = new Date();

  return (
    parsedDate.getFullYear() === today.getFullYear() &&
    parsedDate.getMonth() === today.getMonth() &&
    parsedDate.getDate() === today.getDate()
  );
};

const isCrisisAllocation = (allocation: ICrisisCallsAllocatedClientsList): boolean =>
  allocation.isDispatch || Boolean(allocation.crisisCallId);

const shouldAddAllocation = (allocation: ICrisisCallsAllocatedClientsList): boolean =>
  isCrisisAllocation(allocation) || isTodayInLocalTimezone(allocation.allocationDate);

export const recalculateCrisisStatus = (member: ICrisisCallTeamMember): void => {
  if (member.allocatedClients.some(client => Boolean(client.visitStartTime))) {
    member.crisisStatus = CrisisStatus.SERVICE_STARTED;
    return;
  }

  const todaysAllocatedClients = member.allocatedClients.filter(client =>
    isTodayInLocalTimezone(client.allocationDate),
  );

  // CrisisStatus.UNALLOCATED if zero client allocations assigned for today
  member.crisisStatus = todaysAllocatedClients.length
    ? CrisisStatus.ALLOCATED
    : CrisisStatus.UNALLOCATED;
};

export const upsertTeamMemberAllocations = (
  existingMember: ICrisisCallTeamMember,
  incomingMember: ICrisisCallTeamMember,
): void => {
  existingMember.firstName = incomingMember.firstName;
  existingMember.lastName = incomingMember.lastName;
  existingMember.photo = incomingMember.photo;
  existingMember.photoThumbnail = incomingMember.photoThumbnail;

  const allocationsByKey = new Map(
    existingMember.allocatedClients.map(allocation => [getAllocationKey(allocation), allocation]),
  );

  incomingMember.allocatedClients.filter(shouldAddAllocation).forEach(allocation => {
    allocationsByKey.set(getAllocationKey(allocation), allocation);
  });

  existingMember.allocatedClients = Array.from(allocationsByKey.values());
  recalculateCrisisStatus(existingMember);
};

export const createTeamMemberWithFilteredAllocations = (
  member: ICrisisCallTeamMember,
): ICrisisCallTeamMember => {
  const nextMember = {
    ...member,
    allocatedClients: member.allocatedClients.filter(shouldAddAllocation),
  };

  recalculateCrisisStatus(nextMember);

  return nextMember;
};

export const createTeamMemberWithCalculatedStatus = (
  member: ICrisisCallTeamMember,
): ICrisisCallTeamMember => {
  const nextMember = { ...member };

  recalculateCrisisStatus(nextMember);

  return nextMember;
};

export const removeAllocations = (
  member: ICrisisCallTeamMember,
  predicate: (allocation: ICrisisCallsAllocatedClientsList) => boolean,
): void => {
  member.allocatedClients = member.allocatedClients.filter(allocation => !predicate(allocation));
  recalculateCrisisStatus(member);
};

export const matchesAllocation = (
  allocation: ICrisisCallsAllocatedClientsList,
  clientId?: number,
  crisisCallId?: number,
): boolean =>
  !crisisCallId ? allocation.clientId === clientId : allocation.crisisCallId === crisisCallId;
